import React, { useEffect, useState } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Box, Button, Card, CardContent, Typography, IconButton, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/EditOutlined';
import AddEditCompanyLink from './AddEditCompanyLink';
import FacebookIcon from 'assets/images/icons/socials/facebook.svg';
import LinkedInIcon from 'assets/images/icons/socials/linkedin.svg';
import InstagramIcon from 'assets/images/icons/socials/instagram.svg';
import YoutubeIcon from 'assets/images/icons/socials/youtube.svg';
import TwitterIcon from 'assets/images/icons/socials/xtwitter.svg';
import LinkIcon from 'assets/images/icons/socials/other.svg';
import LockIcon from 'assets/images/icons/Lock.svg';
import FrameIcon from 'assets/images/icons/Frame.svg';

import { classifyLinkType, embedHttpsIfUrlDoesnotHaveIt } from 'helper/string.helper';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation } from '@apollo/client';
import { ADD_COMPANY_LINK, DELETE_COMPANY_LINK, DRAG_AND_DROP_COMPANY_LINK, UPDATE_COMPANY_LINK } from 'schemas/mutation';
import { SET_COMPANY_DATA, SET_LOADING, SET_TOAST_OBJECT } from 'store/actions';
import { PermissionTypes } from 'Components/PermissionWrapper/constants';
import { hasNoPermissionToEdit } from 'helper/permission.helper';

function CompanyLink() {
    const [links, setLinks] = useState([]);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedLink, setSelectedLink] = useState(null);
    const { pagePermissions } = useSelector((state) => state.authentication);
    const { companyData } = useSelector((state) => state.customization);
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({ _id: '', title: '', url: '', priorityOrder: 0, type: '', locked: false });
    const [addCompanyLink] = useMutation(ADD_COMPANY_LINK);
    const [updateCompanyLink] = useMutation(UPDATE_COMPANY_LINK);
    const [deleteCompanyLink] = useMutation(DELETE_COMPANY_LINK);
    const [dragAndDropCompanyLink] = useMutation(DRAG_AND_DROP_COMPANY_LINK);
    const dispatch = useDispatch();
    const maxLinksAllowed = 6 - links.length;
    const hasEditNoPermission = hasNoPermissionToEdit(PermissionTypes.companyDetails, pagePermissions);
    useEffect(() => {
        if (companyData?._id) {
            setLinks(companyData?.companyLinks || []);
        }
    }, [companyData]);

    const onDragAndDropCompanyLink = async (newItems, previousItems) => {
        if (!newItems || !newItems?.length) return;
        const sanitizedItems = newItems.map(({ __typename, ...rest }) => rest);
        try {
            await dragAndDropCompanyLink({
                variables: {
                    dragAndDropUpdateCompanyLinkInput: {
                        companyLinks: [...sanitizedItems]
                    }
                }
            });
        } catch (e) {
            setNewCompanyLinks(previousItems);
            dispatch({
                type: SET_TOAST_OBJECT,
                toastObject: {
                    status: true,
                    variant: 'error',
                    message: e.message
                }
            });
        } finally {
            dispatch({ type: SET_LOADING, loading: false });
        }
    };

    const handleOnDragEnd = (result) => {
        if (!result.destination) return;
        const previousItems = [...links];
        const items = Array.from(links);
        const [reorderedItem] = items.splice(result.source.index, 1);
        if (reorderedItem?.locked) {
            return;
        }
        items.splice(result.destination.index, 0, reorderedItem);

        const newItems = items.map((item, index) => ({ ...item, priorityOrder: index + 1 }));
        setNewCompanyLinks(newItems);
        onDragAndDropCompanyLink(newItems, previousItems);
    };

    const onDeleteCompanyLink = async () => {
        if (!selectedLink?._id) return;
        dispatch({ type: SET_LOADING, loading: true });
        try {
            const response = await deleteCompanyLink({
                variables: {
                    deleteCompanyLinkInput: {
                        linkId: selectedLink?._id
                    }
                }
            });
            if (response?.data) {
                const { deleteCompanyLink } = response.data;
                setNewCompanyLinks(deleteCompanyLink?.companyLinks || []);
            }
            handleCloseMenu();
        } catch (e) {
            dispatch({
                type: SET_TOAST_OBJECT,
                toastObject: {
                    status: true,
                    variant: 'error',
                    message: e.message
                }
            });
        } finally {
            dispatch({ type: SET_LOADING, loading: false });
        }
    };

    const onUpdateCompanyLink = async (newFormData) => {
        if (!newFormData._id) return;
        dispatch({ type: SET_LOADING, loading: true });
        try {
            const response = await updateCompanyLink({
                variables: {
                    updateCompanyLinkInput: {
                        title: newFormData.title,
                        type: newFormData.type,
                        url: newFormData.url,
                        linkId: newFormData._id
                    }
                }
            });
            if (response?.data) {
                const { updateCompanyLink } = response.data;
                setNewCompanyLinks(updateCompanyLink?.companyLinks || []);
            }
            handleCancel();
        } catch (e) {
            dispatch({
                type: SET_TOAST_OBJECT,
                toastObject: {
                    status: true,
                    variant: 'error',
                    message: e.message
                }
            });
        } finally {
            dispatch({ type: SET_LOADING, loading: false });
        }
    };

    const sortLinksByPriority = (newLinks) => {
        return newLinks.sort((a, b) => a.priorityOrder - b.priorityOrder);
    };
    const setNewCompanyLinks = (modifiedCompanyLinks) => {
        const modifiedLinks = sortLinksByPriority(modifiedCompanyLinks);
        setLinks(modifiedLinks);
        let websiteUrl = companyData?.websiteUrl || '';
        if (formData.locked) {
            websiteUrl = formData.url;
        }
        dispatch({ type: SET_COMPANY_DATA, companyData: { ...companyData, websiteUrl, companyLinks: [...modifiedLinks] } });
    };
    const onAddCompanyLink = async (newFormData) => {
        dispatch({ type: SET_LOADING, loading: true });
        try {
            const response = await addCompanyLink({
                variables: {
                    addCompanyLinkInput: {
                        title: newFormData.title,
                        type: newFormData.type,
                        url: newFormData.url
                    }
                }
            });
            if (response?.data) {
                const { addCompanyLinks } = response.data;
                setNewCompanyLinks(addCompanyLinks?.companyLinks || []);
            }
            handleCancel();
        } catch (e) {
            dispatch({
                type: SET_TOAST_OBJECT,
                toastObject: {
                    status: true,
                    variant: 'error',
                    message: e.message
                }
            });
        } finally {
            dispatch({ type: SET_LOADING, loading: false });
        }
    };

    const handleMoreClick = (event, link) => {
        setAnchorEl(event.currentTarget);
        setSelectedLink(link);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
        setTimeout(() => setSelectedLink(null), 0);
    };

    const handleEdit = () => {
        setFormData(selectedLink);
        setIsEditing(true);
        handleCloseMenu();
    };

    const handleFormSubmit = () => {
        const type = classifyLinkType(formData.url);
        console.log(type);
        if (formData._id) {
            onUpdateCompanyLink({ ...formData, type });
        } else {
            onAddCompanyLink({ ...formData, type });
        }
        handleCloseMenu();
    };

    const handleCancel = () => {
        setIsEditing(false);
        setFormData({ _id: '', title: '', url: '', priorityOrder: 0, type: '', locked: false });
    };

    const handleAddLink = () => {
        setFormData({ _id: '', title: '', url: '', priorityOrder: 0, type: '', locked: false });
        setIsEditing(true);
    };

    const onClickLink = (url) => {
        const newUrl = embedHttpsIfUrlDoesnotHaveIt(url);
        window.open(newUrl, '_blank').focus();
    };

    const getIcon = (type) => {
        switch (type) {
            case 'facebook':
                return FacebookIcon;
            case 'instagram':
                return InstagramIcon;
            case 'twitter':
                return TwitterIcon;
            case 'linkedin':
                return LinkedInIcon;
            case 'youtube':
                return YoutubeIcon;
            default:
                return LinkIcon;
        }
    };

    return (
        <Box sx={{ paddingTop: '20px', gap: 3 }}>
            <Typography variant="h3" gutterBottom>
                Company links
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: '20px' }}>
                Enter company links you would like your investors to visit. The order of the links here are reflected in your company's
                mobile profile.
            </Typography>
            {/**Link first */}
            {links.length ? (
                <>
                    {' '}
                    <Card
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            mb: 2,
                            width: '100%',
                            backgroundColor: '#ffffff',
                            border: '1px solid #DEDEDF',
                            '&:hover': {
                                backgroundColor: '#f0f0f0'
                            }
                        }}
                    >
                        <CardContent
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                flexGrow: 1,
                                padding: '21px 16px'
                            }}
                        >
                            <img src={links?.[0]?.locked ? LockIcon : FrameIcon} alt="prefix-icon" />
                            <Button
                                onClick={() => onClickLink(links?.[0]?.url)}
                                disabled={hasEditNoPermission}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    padding: 0,
                                    margin: 0,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center'
                                }}
                            >
                                <img src={getIcon(links?.[0]?.type)} alt="type" style={{ marginLeft: '20px' }} />
                            </Button>

                            <Typography
                                variant="body1"
                                sx={{ ml: 2, cursor: 'pointer' }}
                                onClick={() => onClickLink(links?.[0]?.url)}
                                fontWeight="600"
                                color="#00214D"
                            >
                                {links?.[0]?.title}
                            </Typography>
                        </CardContent>
                        <IconButton
                            disabled={hasEditNoPermission}
                            sx={{ mr: 2, transform: 'rotate(90deg)' }}
                            onClick={(event) => handleMoreClick(event, links?.[0])}
                        >
                            <MoreVertIcon />
                        </IconButton>
                    </Card>
                    {isEditing && formData._id === links?.[0]?._id && (
                        <AddEditCompanyLink
                            isEditing={isEditing}
                            formData={formData}
                            setFormData={setFormData}
                            handleFormSubmit={handleFormSubmit}
                            handleCancel={handleCancel}
                        />
                    )}
                </>
            ) : (
                ''
            )}
            <DragDropContext onDragEnd={handleOnDragEnd}>
                <Droppable droppableId="links" isDropDisabled={hasEditNoPermission}>
                    {(provided) => (
                        <div {...provided.droppableProps} ref={provided.innerRef}>
                            {links.map(
                                ({ _id, title, type, url, priorityOrder, locked }, index) =>
                                    index !== 0 && (
                                        <Draggable
                                            key={_id}
                                            draggableId={_id}
                                            index={index}
                                            isDragDisabled={locked || hasEditNoPermission}
                                            disableInteractiveElementBlocking={locked}
                                        >
                                            {(provided) => (
                                                <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                                                    <Card
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            mb: 2,
                                                            width: '100%',
                                                            backgroundColor: '#ffffff',
                                                            border: '1px solid #DEDEDF',
                                                            '&:hover': {
                                                                backgroundColor: '#f0f0f0'
                                                            }
                                                        }}
                                                    >
                                                        <CardContent
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                flexGrow: 1,
                                                                padding: '21px 16px'
                                                            }}
                                                        >
                                                            <img src={locked ? LockIcon : FrameIcon} alt="prefix-icon" />
                                                            <Button
                                                                onClick={() => onClickLink(url)}
                                                                style={{
                                                                    background: 'none',
                                                                    border: 'none',
                                                                    padding: 0,
                                                                    margin: 0,
                                                                    cursor: 'pointer',
                                                                    display: 'flex',
                                                                    alignItems: 'center'
                                                                }}
                                                            >
                                                                <img src={getIcon(type)} alt="type" style={{ marginLeft: '20px' }} />
                                                            </Button>

                                                            <Typography
                                                                variant="body1"
                                                                sx={{
                                                                    ml: 2,
                                                                    cursor: 'pointer'
                                                                }}
                                                                fontWeight="600"
                                                                color="#00214D"
                                                                onClick={() => onClickLink(url)}
                                                            >
                                                                {title}
                                                            </Typography>
                                                        </CardContent>
                                                        <IconButton
                                                            disabled={hasEditNoPermission}
                                                            sx={{ mr: 2, transform: 'rotate(90deg)' }}
                                                            onClick={(event) =>
                                                                handleMoreClick(event, { _id, title, url, type, priorityOrder, locked })
                                                            }
                                                        >
                                                            <MoreVertIcon />
                                                        </IconButton>
                                                    </Card>
                                                    {isEditing && formData._id === _id && (
                                                        <AddEditCompanyLink
                                                            isEditing={isEditing}
                                                            formData={formData}
                                                            setFormData={setFormData}
                                                            handleFormSubmit={handleFormSubmit}
                                                            handleCancel={handleCancel}
                                                        />
                                                    )}
                                                </div>
                                            )}
                                        </Draggable>
                                    )
                            )}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
            {!isEditing && links.length < 6 && (
                <Button
                    variant="outlined"
                    className="alternate-reversed"
                    startIcon={<AddIcon />}
                    disabled={hasEditNoPermission}
                    onClick={handleAddLink}
                    sx={{
                        '&.alternate-reversed': {
                            padding: '9px 23px 9px 12px',
                            fontSize: '0.875rem',
                            borderRadius: '4px',
                            lineHeight: '1.2',

                            '.MuiButton-startIcon': {
                                marginLeft: 0,
                                marginRight: '12px'
                            },

                            svg: {
                                width: '24px',
                                height: '24px'
                            }
                        }
                    }}
                >
                    Add link
                </Button>
            )}
            {isEditing && !formData._id && (
                <AddEditCompanyLink
                    isEditing={isEditing}
                    formData={formData}
                    setFormData={setFormData}
                    handleFormSubmit={handleFormSubmit}
                    handleCancel={handleCancel}
                />
            )}
            {maxLinksAllowed > 0 && (
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    Maximum of {maxLinksAllowed} additional links allowed.
                </Typography>
            )}
            {maxLinksAllowed === 0 && (
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    You have reached the maximum no. of additional links allowed.
                </Typography>
            )}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl) && !hasEditNoPermission}
                onClose={handleCloseMenu}
                sx={{
                    '.MuiList-root': {
                        padding: '0',
                        '.MuiPopover-paper': {
                            boxShadow: 'none'
                        },
                        '.MuiButtonBase-root': {
                            padding: '14px 24px',

                            '&.delete': {
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 62, 74, 0.1)'
                                },
                                '.MuiListItemIcon-root ': {
                                    svg: {
                                        path: {
                                            fill: '#FF3E4A'
                                        }
                                    }
                                },
                                '.MuiListItemText-root': {
                                    '.MuiTypography-root': {
                                        color: '#FF3E4A'
                                    }
                                }
                            },
                            '.MuiListItemIcon-root ': {
                                minWidth: '24px',
                                marginRight: '8px',
                                svg: {
                                    width: '24px',
                                    height: '24px',
                                    path: {
                                        fill: '#7D7D82'
                                    }
                                }
                            },
                            '.MuiListItemText-root': {
                                color: '#7D7D82',
                                '.MuiTypography-root': {
                                    fontWeight: 500,
                                    color: '#7D7D82'
                                }
                            }
                        }
                    }
                }}
            >
                <MenuItem disabled={hasEditNoPermission} onClick={handleEdit}>
                    <ListItemIcon>
                        <EditIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Edit link" />
                </MenuItem>
                {!selectedLink?.locked && (
                    <MenuItem disabled={hasEditNoPermission} onClick={onDeleteCompanyLink} className="delete">
                        <ListItemIcon>
                            <DeleteIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Delete link" />
                    </MenuItem>
                )}
            </Menu>
        </Box>
    );
}

export default CompanyLink;
